import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-visor-archivo',
  template: `
    <div class="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <!-- Header moderno -->
      <div class="bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg">
        <div class="px-6 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"
            >
              <i [class]="getFileIcon()" class="text-white text-lg"></i>
            </div>
            <div>
              <h1 class="text-white font-semibold text-lg">
                {{ getFileName() }}
              </h1>
              <p class="text-blue-100 text-sm">{{ getFileType() }}</p>
            </div>
          </div>
          <div class="flex gap-3">
            <button
              (click)="descargarArchivo()"
              class="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all duration-200 backdrop-blur-sm"
            >
              <i class="fa-solid fa-download"></i>
              Descargar
            </button>
            <button
              (click)="abrirEnNuevaVentana()"
              class="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all duration-200 backdrop-blur-sm"
            >
              <i class="fa-solid fa-external-link-alt"></i>
              Abrir
            </button>
            <button
              (click)="volverAtras()"
              class="flex items-center gap-2 bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition-all duration-200 font-medium"
            >
              <i class="fa-solid fa-arrow-left"></i>
              Volver
            </button>
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div *ngIf="loading" class="flex-grow flex items-center justify-center">
        <div class="text-center">
          <div
            class="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"
          ></div>
          <p class="text-gray-600 dark:text-gray-400">Cargando archivo...</p>
        </div>
      </div>

      <!-- Error state -->
      <div
        *ngIf="error && !loading"
        class="flex-grow flex items-center justify-center"
      >
        <div class="text-center p-8 max-w-md">
          <div
            class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <i
              class="fa-solid fa-exclamation-triangle text-red-500 text-2xl"
            ></i>
          </div>
          <h2
            class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2"
          >
            Error al cargar el archivo
          </h2>
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            {{ errorMessage }}
          </p>
          <div class="flex gap-3 justify-center">
            <button
              (click)="reintentar()"
              class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Reintentar
            </button>
            <button
              (click)="volverAtras()"
              class="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
            >
              Volver atrás
            </button>
          </div>
        </div>
      </div>

      <!-- Content area -->
      <div *ngIf="!loading && !error" class="flex-grow overflow-hidden">
        <!-- PDF Viewer -->
        <div *ngIf="fileType === 'pdf'" class="h-full">
          <!-- Método 1: Embed nativo -->
          <div *ngIf="!pdfError" class="h-full">
            <embed
              [src]="safeArchivoUrl"
              type="application/pdf"
              class="w-full h-full"
              [attr.aria-label]="'Documento PDF: ' + getFileName()"
              (error)="onPdfError()"
            />
          </div>

          <!-- Fallback: Visor alternativo -->
          <div
            *ngIf="pdfError"
            class="h-full flex items-center justify-center bg-gray-50"
          >
            <div class="text-center p-8 max-w-md">
              <div
                class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <i class="fa-solid fa-file-pdf text-red-500 text-3xl"></i>
              </div>
              <h3 class="text-xl font-semibold text-gray-700 mb-2">
                {{ getFileName() }}
              </h3>
              <p class="text-gray-600 mb-6">
                El PDF no se puede mostrar directamente en el navegador. Puedes
                descargarlo o abrirlo en una nueva ventana.
              </p>
              <div class="flex gap-3 justify-center">
                <button
                  (click)="descargarArchivo()"
                  class="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  <i class="fa-solid fa-download"></i>
                  Descargar PDF
                </button>
                <button
                  (click)="abrirEnNuevaVentana()"
                  class="flex items-center gap-2 bg-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-400 transition font-medium"
                >
                  <i class="fa-solid fa-external-link-alt"></i>
                  Abrir en nueva ventana
                </button>
              </div>

              <!-- Opción adicional: Visor en línea -->
              <div class="mt-6 pt-6 border-t border-gray-200">
                <p class="text-sm text-gray-500 mb-3">
                  O intenta con un visor en línea:
                </p>
                <button
                  (click)="abrirConGoogleDocs()"
                  class="text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  Abrir con Google Docs Viewer
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Image Viewer -->
        <div
          *ngIf="fileType === 'image'"
          class="h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800"
        >
          <div class="max-w-full max-h-full p-4">
            <img
              [src]="archivoUrl"
              [alt]="getFileName()"
              class="max-w-full max-h-full object-contain shadow-lg rounded-lg"
              (error)="onImageError()"
            />
          </div>
        </div>

        <!-- Text Viewer -->
        <div *ngIf="fileType === 'text'" class="h-full overflow-auto">
          <div class="max-w-4xl mx-auto p-6">
            <div
              class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div class="p-6">
                <pre
                  class="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono leading-relaxed"
                  >{{ textContent }}</pre
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Office Viewer (Word, Excel, PowerPoint) -->
        <div
          *ngIf="
            fileType === 'word' ||
            fileType === 'excel' ||
            fileType === 'powerpoint'
          "
          class="h-full flex flex-col"
        >
          <!-- Información del visor -->
          <div
            class="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between"
          >
            <div class="flex items-center gap-2">
              <i class="fa-brands fa-google text-blue-600"></i>
              <span class="text-sm font-medium text-gray-700"
                >Google Docs Viewer</span
              >
            </div>
            <p class="text-xs text-gray-500">
              Si el documento no se visualiza correctamente, descárgalo o ábrelo
              en nueva ventana
            </p>
          </div>

          <!-- Iframe del visor -->
          <div class="flex-grow">
            <iframe
              [src]="officeViewerUrl"
              class="w-full h-full border-0"
              [attr.aria-label]="'Visor de documento: ' + getFileName()"
            ></iframe>
          </div>
        </div>

        <!-- Document Viewer (Other files) -->
        <div
          *ngIf="fileType === 'document'"
          class="h-full flex items-center justify-center"
        >
          <div class="text-center p-8 max-w-md">
            <div
              class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <i [class]="getFileIcon()" class="text-blue-600 text-3xl"></i>
            </div>
            <h3
              class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              {{ getFileName() }}
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-6">
              Este tipo de archivo requiere una aplicación externa para
              visualizarse correctamente.
            </p>
            <div class="flex gap-3 justify-center">
              <button
                (click)="descargarArchivo()"
                class="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
              >
                <i class="fa-solid fa-download"></i>
                Descargar archivo
              </button>
              <button
                (click)="abrirEnNuevaVentana()"
                class="flex items-center gap-2 bg-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-400 transition font-medium"
              >
                <i class="fa-solid fa-external-link-alt"></i>
                Abrir en nueva ventana
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class VisorArchivoComponent implements OnInit {
  archivoUrl: string = '';
  safeArchivoUrl: SafeResourceUrl | null = null;
  fileName: string = '';
  fileType:
    | 'pdf'
    | 'image'
    | 'text'
    | 'document'
    | 'word'
    | 'excel'
    | 'powerpoint' = 'document';
  loading: boolean = true;
  error: boolean = false;
  errorMessage: string = '';
  textContent: string = '';
  pdfError: boolean = false;
  officeViewerUrl: SafeResourceUrl | null = null;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    const base64Url = this.route.snapshot.queryParamMap.get('archivo');
    if (base64Url) {
      try {
        this.archivoUrl = decodeURIComponent(base64Url);
        this.fileName = this.extractFileName(this.archivoUrl);
        this.fileType = this.determineFileType(this.fileName);
        this.loadFile();
      } catch (error) {
        this.error = true;
        this.errorMessage = 'Error al decodificar la URL del archivo';
        this.loading = false;
      }
    } else {
      this.error = true;
      this.errorMessage = 'No se proporcionó la URL del archivo';
      this.loading = false;
    }
  }

  private extractFileName(url: string): string {
    try {
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      // Remover parámetros de query si existen
      const cleanFileName = fileName.split('?')[0] || 'archivo';

      // Si el archivo tiene el formato nombre_uuid.extension, extraer solo el nombre original
      if (
        cleanFileName.includes('_') &&
        cleanFileName.match(/.*_[a-f0-9]{8}\./)
      ) {
        const parts = cleanFileName.split('_');
        if (parts.length >= 2) {
          // Reconstruir el nombre original (puede tener guiones internos)
          const originalName = parts.slice(0, -1).join('_');
          const extension = cleanFileName.substring(
            cleanFileName.lastIndexOf('.')
          );
          return originalName + extension;
        }
      }

      return cleanFileName;
    } catch {
      return 'archivo';
    }
  }

  private determineFileType(
    fileName: string
  ): 'pdf' | 'image' | 'text' | 'document' | 'word' | 'excel' | 'powerpoint' {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    if (extension === 'pdf') {
      return 'pdf';
    } else if (
      ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)
    ) {
      return 'image';
    } else if (
      [
        'txt',
        'csv',
        'log',
        'md',
        'json',
        'xml',
        'html',
        'css',
        'js',
        'ts',
      ].includes(extension)
    ) {
      return 'text';
    } else if (['doc', 'docx'].includes(extension)) {
      return 'word';
    } else if (['xls', 'xlsx'].includes(extension)) {
      return 'excel';
    } else if (['ppt', 'pptx'].includes(extension)) {
      return 'powerpoint';
    } else {
      return 'document';
    }
  }

  private loadFile() {
    this.loading = true;
    this.error = false;

    // Sanitizar la URL para uso seguro
    this.safeArchivoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.archivoUrl
    );

    // Para archivos de Office (Word, Excel, PowerPoint), abrir en Google Docs Viewer
    if (['word', 'excel', 'powerpoint'].includes(this.fileType)) {
      const googleDocsUrl = `https://docs.google.com/viewerng/viewer?url=${encodeURIComponent(
        this.archivoUrl
      )}`;
      window.open(googleDocsUrl, '_blank');
      this.loading = false;
      setTimeout(() => this.volverAtras(), 300);
      return;
    }

    // Para archivos de texto, cargar el contenido
    else if (this.fileType === 'text') {
      this.http.get(this.archivoUrl, { responseType: 'text' }).subscribe({
        next: (content) => {
          this.textContent = content;
          this.loading = false;
        },
        error: () => {
          this.error = true;
          this.errorMessage =
            'No se pudo cargar el contenido del archivo de texto';
          this.loading = false;
        },
      });
    } else {
      // Para otros tipos, simplemente marcar como cargado
      this.loading = false;
    }
  }

  getFileName(): string {
    return this.fileName || 'Archivo';
  }

  getFileType(): string {
    const extension = this.fileName.split('.').pop()?.toLowerCase() || '';
    switch (this.fileType) {
      case 'pdf':
        return 'Documento PDF';
      case 'image':
        return 'Imagen';
      case 'text':
        return 'Archivo de texto';
      case 'word':
        return 'Documento Word';
      case 'excel':
        return 'Hoja de cálculo Excel';
      case 'powerpoint':
        return 'Presentación PowerPoint';
      case 'document':
        return `Documento ${extension.toUpperCase()}`;
      default:
        return 'Archivo';
    }
  }

  getFileIcon(): string {
    switch (this.fileType) {
      case 'pdf':
        return 'fa-solid fa-file-pdf text-red-500';
      case 'image':
        return 'fa-solid fa-file-image text-green-500';
      case 'text':
        return 'fa-solid fa-file-lines text-blue-500';
      case 'document':
        const extension = this.fileName.split('.').pop()?.toLowerCase() || '';
        if (['doc', 'docx'].includes(extension)) {
          return 'fa-solid fa-file-word text-blue-600';
        } else if (['xls', 'xlsx'].includes(extension)) {
          return 'fa-solid fa-file-excel text-green-600';
        } else if (['ppt', 'pptx'].includes(extension)) {
          return 'fa-solid fa-file-powerpoint text-orange-600';
        } else {
          return 'fa-solid fa-file text-gray-500';
        }
      default:
        return 'fa-solid fa-file text-gray-500';
    }
  }

  volverAtras() {
    this.location.back();
  }

  descargarArchivo() {
    if (this.archivoUrl) {
      const link = document.createElement('a');
      link.href = this.archivoUrl;
      link.download = this.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  abrirEnNuevaVentana() {
    if (this.archivoUrl) {
      window.open(this.archivoUrl, '_blank');
    }
  }

  reintentar() {
    this.loadFile();
  }

  onImageError() {
    this.error = true;
    this.errorMessage = 'No se pudo cargar la imagen';
    this.loading = false;
  }

  onPdfError() {
    this.pdfError = true;
  }

  abrirConGoogleDocs() {
    if (this.archivoUrl) {
      const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        this.archivoUrl
      )}&embedded=true`;
      window.open(googleDocsUrl, '_blank');
    }
  }
}
